import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Button } from './button';
import { getStripe } from '@/lib/stripe';
import { usePortalInvoicePaymentIntent } from '@/lib/portal-invoices';
import { dollars } from '@/lib/format';

/**
 * Stripe payment for a single portal invoice. Creates a PaymentIntent, mounts
 * the Payment Element, and confirms in place. The invoice is marked paid by the
 * Stripe webhook, so on success we signal the parent to refetch.
 */
export function InvoicePayment({
  invoiceId,
  onDone,
  onCancel,
}: {
  invoiceId: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const intent = usePortalInvoicePaymentIntent(invoiceId);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [canSaveCard, setCanSaveCard] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recreate the intent when the save-card choice changes: whether the card is
  // stored for future invoices is fixed at intent creation, so opting in mints
  // a fresh intent that attaches the customer and flags off-session reuse.
  useEffect(() => {
    setClientSecret(null);
    intent
      .mutateAsync(saveCard)
      .then((res) => {
        setClientSecret(res.client_secret);
        setPublishableKey(res.publishable_key);
        setAmountCents(res.amount_cents);
        setCanSaveCard(res.can_save_card);
      })
      .catch(() => setError('Could not start the payment. Please try again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveCard]);

  const stripePromise = useMemo(
    () => (publishableKey ? getStripe(publishableKey) : null),
    [publishableKey],
  );

  if (error) {
    return <p className="text-sm text-[var(--color-danger)]">{error}</p>;
  }

  return (
    <div className="space-y-3">
      {!clientSecret || !stripePromise ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Preparing secure payment…</p>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
          <PayForm
            amountCents={amountCents}
            onDone={onDone}
            onCancel={onCancel}
            saveCard={canSaveCard ? saveCard : null}
            onSaveCardChange={setSaveCard}
          />
        </Elements>
      )}
    </div>
  );
}

function PayForm({
  amountCents,
  onDone,
  onCancel,
  saveCard,
  onSaveCardChange,
}: {
  amountCents: number | null;
  onDone: () => void;
  onCancel: () => void;
  /** Current save-card choice, or null when saving isn't available. */
  saveCard: boolean | null;
  onSaveCardChange: (value: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message ?? 'Payment could not be completed.');
      setSubmitting(false);
      return;
    }

    onDone();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {saveCard !== null ? (
        <label className="flex items-center gap-2 text-sm text-[var(--color-foreground-muted)]">
          <input
            type="checkbox"
            className="size-4"
            checked={saveCard}
            disabled={submitting}
            onChange={(e) => onSaveCardChange(e.target.checked)}
          />
          <span>Save this card for future invoices</span>
        </label>
      ) : null}
      {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !stripe}>
          {submitting ? 'Processing…' : amountCents != null ? `Pay ${dollars(amountCents)}` : 'Pay'}
        </Button>
      </div>
    </form>
  );
}
